import React, { memo, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import WistiaPlayer from 'react-player/wistia';
import YouTubePlayer from 'react-player/youtube';
import throttle from 'lodash/throttle';
import PropTypes from 'prop-types';

import { cloneDeep } from '../../../../utils';
import { COURSE_STATUS } from '../../../../constants';

import { updateVideoProgressRequest } from './actions';

const Video = ({ lesson, dataUserLessonId, product }) => {
     const dispatch = useDispatch();
     const { userLessonId, courseId } = useParams();

     const videoRef = useRef();
     const userLesson = cloneDeep(useSelector((state) => state.subscriber.userLesson));

     const [skipFirstStart, setSkipFirstStart] = useState(true); // To skip the first onStart method of the Wistia video

     const isWistia = lesson.videoUrl.includes('wistia.com');
     const isYoutube = lesson.videoUrl.includes('youtube.com') || lesson.videoUrl.includes('youtu.be');

     const isCompleteLesson = userLesson?.status === COURSE_STATUS.COMPLETED;
     const videoProgressed = userLesson?.videoProgressed ?? 0;
     const paramUserLessonId = dataUserLessonId || userLessonId;

     const playerConfig = {
          wistia: {
               // https://wistia.com/support/developers/embed-options
               wistia: {
                    options: {
                         preload: 'auto',
                         time: 0,
                    },
               },
          },
          youtube: {
               // https://developers.google.com/youtube/player_parameters?playerVersion=HTML5
               youtube: {
                    playerVars: {
                         rel: 0,
                    },
               },
          },
     };

     const handleVideoReady = () => {
          let thePlayer;

          if (videoRef.current) {
               thePlayer = videoRef.current?.player?.player?.player;
          }

          if (!thePlayer) {
               return;
          }

          const videoDuration = videoRef.current.getDuration();
          const videoProgressTime = videoProgressed === 100 ? 0 : Math.floor((parseInt(videoProgressed) * videoDuration) / 100);

          if (isWistia) {
               thePlayer.time(videoProgressTime);
          }

          if (isYoutube && videoProgressTime !== 0) {
               thePlayer.seekTo(videoProgressTime);
          }

          // Son Nguyen comment unused
          // dispatch(setLessonVideoRef(thePlayer));
     };

     const handleVideoStart = () => {
          if (isCompleteLesson) {
               return;
          }

          // Wistia video fires this event on first load -> skipFirstStart to block
          if (skipFirstStart && isWistia) {
               setSkipFirstStart(false);
               return;
          }

          if (videoProgressed !== 0) {
               return;
          }

          if (paramUserLessonId) {
               dispatch(updateVideoProgressRequest({ userLessonId: paramUserLessonId, courseId, product }, { videoProgressed: 1 }));
          }
     };

     const handleVideoProgress = throttle(
          (e) => {
               if (isCompleteLesson) {
                    return;
               }

               // Wistia video fires this event on first load -> skipFirstStart to block
               if (skipFirstStart && isWistia) {
                    return;
               }

               let percentage = Math.round(e.played * 100);
               percentage = percentage === 0 && e.played > 0 ? 1 : percentage;

               if (percentage === videoProgressed) {
                    return;
               }

               if (paramUserLessonId && percentage > 0 && percentage < 99) {
                    dispatch(updateVideoProgressRequest({ userLessonId: paramUserLessonId, courseId, product }, { videoProgressed: percentage }));
               }
          },
          10000,
          { trailing: false }
     );

     const handleVideoEnd = () => {
          if (isCompleteLesson) {
               return;
          }

          if (videoProgressed !== 100 && paramUserLessonId) {
               dispatch(updateVideoProgressRequest({ userLessonId: paramUserLessonId, courseId, product }, { videoProgressed: 100 }));
          }
     };

     const handlePauseVideo = () => {
          let thePlayer;

          if (videoRef.current) {
               thePlayer = videoRef.current?.player?.player?.player;
          }

          if (thePlayer && thePlayer.pauseVideo) {
               thePlayer.pauseVideo();
          }
     };

     useEffect(() => {
          // unmount lesson video ref
          // Son Nguyen comment unused
          // dispatch(setLessonVideoRef(null));

          return () => {
               handlePauseVideo();
               videoRef.current = null;
          };
     }, []); // eslint-disable-line react-hooks/exhaustive-deps

     return (
          <div className="video-wrapper">
               <div className="video">
                    {isWistia && (
                         <WistiaPlayer
                              ref={videoRef}
                              className="the-player"
                              url={lesson.videoUrl}
                              controls={true}
                              width="100%"
                              height="100%"
                              config={playerConfig.wistia}
                              onPlay={handleVideoStart}
                              onReady={handleVideoReady}
                              onProgress={handleVideoProgress}
                              onEnded={handleVideoEnd}
                         />
                    )}
                    {isYoutube && (
                         <YouTubePlayer
                              ref={videoRef}
                              className="the-player"
                              url={lesson.videoUrl}
                              controls={true}
                              width="100%"
                              height="100%"
                              config={playerConfig.youtube}
                              onPlay={handleVideoStart}
                              onReady={handleVideoReady}
                              onProgress={handleVideoProgress}
                              onEnded={handleVideoEnd}
                         />
                    )}
               </div>
          </div>
     );
};

Video.propTypes = {
     lesson: PropTypes.object,
     product: PropTypes.object,
};

export default memo(Video);
